import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Bot, Phone, ArrowRight, Settings, LogOut } from 'lucide-react';
import { Button } from '../components/ui/button';

const PRODUCT_INFO = {
  agent9: {
    name: 'Agent9',
    tagline: 'AI assistant for real estate agents',
    description: 'Manage leads, AI phone answering, appointment booking, and call analytics.',
    icon: Bot,
    color: 'emerald',
    route: '/dashboard',
  },
  voicedesk: {
    name: 'VoiceDesk',
    tagline: 'AI-powered business phone routing',
    description: 'Smart IVR, call routing, department management, and AI receptionist.',
    icon: Phone,
    color: 'blue',
    route: '/voicedesk/dashboard',
  },
  tele_sale: {
    name: 'Tele Sale',
    tagline: 'Company phone system for sales teams',
    description: 'Staff seats with dedicated US numbers, AI front desk, leads, callbacks and an AI sales brain.',
    icon: Phone,
    color: 'blue',
    route: '/telesales',
  },
};

export default function ProductSelectorPage() {
  const navigate = useNavigate();
  const { user, switchProduct, logout } = useAuth();
  const products = user?.products || [];

  const handleSelect = (productId) => {
    switchProduct(productId);
    const info = PRODUCT_INFO[productId];
    if (info) {
      navigate(info.route);
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="product-selector-page">
      <div className="w-full max-w-xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg overflow-hidden shrink-0">
              <img src="/assets/agent9-icon.png" alt="Agent9" className="w-full h-full object-contain" />
            </div>
            <span className="text-xl font-bold text-foreground tracking-tight">Agent9</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/settings/account" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="account-settings-link">
              <Settings size={18} />
            </Link>
            <button onClick={logout} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="logout-btn">
              <LogOut size={18} />
            </button>
          </div>
        </div>

        {/* Welcome */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-foreground" data-testid="selector-heading">Choose your workspace</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Welcome back{user?.full_name ? `, ${user.full_name.split(' ')[0]}` : ''}
          </p>
        </div>

        {/* Product Cards */}
        <div className="grid grid-cols-1 gap-4">
          {products.map(pid => {
            const p = PRODUCT_INFO[pid];
            if (!p) return null;
            const Icon = p.icon;
            const isDefault = user?.default_product === pid;
            const borderHover = p.color === 'emerald'
              ? 'hover:border-emerald-500/60 hover:shadow-emerald-500/5'
              : 'hover:border-blue-500/60 hover:shadow-blue-500/5';
            const iconBg = p.color === 'emerald' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-blue-500/10 text-blue-400';
            const btnClass = p.color === 'emerald'
              ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
              : 'bg-blue-500 hover:bg-blue-600 text-white';

            return (
              <button key={pid} onClick={() => handleSelect(pid)}
                className={`w-full text-left p-6 rounded-xl border border-border bg-card backdrop-blur-sm transition-all hover:shadow-lg ${borderHover}`}
                data-testid={`select-product-${pid}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${iconBg}`}>
                      <Icon size={24} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-foreground">{p.name}</h3>
                        {isDefault && (
                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground bg-muted px-1.5 py-0.5 rounded">Default</span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-0.5">{p.description}</p>
                    </div>
                  </div>
                  <div className={`flex items-center gap-1 text-sm font-medium px-4 py-2 rounded-lg ${btnClass}`}>
                    Open <ArrowRight size={14} />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
